import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, DataTable, FilterBar, FilterChips, IconButton, NumberField, Pagination, Pill, TextField, type Column } from "@atlas/ui/web";
import { POR_PAGINA_PADRAO, recorteDoServidor } from "../../lib/recorte";
import { useDebouncedValue } from "../../lib/use-debounce";
import { useFiltros } from "../../lib/use-filtros";
import { atlas } from "../../lib/sdk";
import type { BancoOferta, BancoOfertaInput, BancoOfertaFiltro, BancoOfertaTipo } from "@atlas/sdk";
import { fmtBRL } from "../../lib/banco-propostas";

const VINCULOS = ["CLT", "ESTATUTARIO", "COMISSIONADO", "APOSENTADO", "PENSIONISTA"];
const SITUACOES = ["ATIVO", "FERIAS", "AFASTADO", "LICENCA", "CEDIDO", "DESLIGADO", "APOSENTADO"];

// Salario da coluna "Salário mín." formatado em R$ (pt-BR), sem centavos.
const fmtReais = (n: number) => `R$ ${n.toLocaleString("pt-BR")}`;

/** Catalogo curado de emojis pra o banco escolher. Cada um combina com um tipo
 *  de campanha comum de credito consignado. Ordem = ordem no grid. */
const ICONES = [
  { emoji: "💰", nome: "Dinheiro" },
  { emoji: "💸", nome: "Liberacao rapida" },
  { emoji: "🏦", nome: "Banco" },
  { emoji: "💳", nome: "Cartao" },
  { emoji: "⚡", nome: "Relampago" },
  { emoji: "🔥", nome: "Quente" },
  { emoji: "🎯", nome: "Direcionada" },
  { emoji: "🚀", nome: "Aprovacao rapida" },
  { emoji: "🎁", nome: "Premio" },
  { emoji: "⭐", nome: "Destaque" },
  { emoji: "✨", nome: "Especial" },
  { emoji: "💎", nome: "Premium" },
  { emoji: "📈", nome: "Alto valor" },
  { emoji: "🏠", nome: "Habitacional" },
  { emoji: "🚗", nome: "Veiculo" },
  { emoji: "🎓", nome: "Educacional" },
  { emoji: "⛱️", nome: "Ferias" },
  { emoji: "🎉", nome: "Campanha" },
];

type TabKey = "ativas" | "encerradas";

// `estaAtiva` foi REMOVIDA na feature 006, e a remocao e o ponto: ela decidia
// "esta no ar" com o relogio do NAVEGADOR, e essa decisao agora e do Postgres
// (`ativo AND (expira_em IS NULL OR expira_em > now())`, em consultas-banco.ts),
// porque e ele quem recorta a aba. Manter as duas seria manter duas definicoes
// da mesma regra, uma delas sujeita ao relogio da maquina do operador — e a que
// discordasse produziria uma linha na aba errada, sem erro nenhum.
//
// A coluna "Situacao" da tabela ainda decide o rotulo (Ativa/Encerrada/Expirada)
// no cliente, e isso e' de proposito: ali e' ROTULO de uma linha que o servidor
// ja escolheu, nao criterio de recorte.

/**
 * Rotulo do produto — fonte unica entre a coluna Titulo e os chips de filtro.
 *
 * Estava inline no render da coluna; com o filtro por tipo, chip e coluna com
 * textos diferentes viravam duas dimensoes na cabeca do operador.
 *
 * `undefined` conta como `credito_novo`: e' o que a coluna `tipo` significa
 * quando esta nula (ofertas gravadas antes do campo existir). A rota normaliza
 * do mesmo jeito, com `coalesce(tipo, 'credito_novo')`.
 */
export function rotuloTipo(t?: BancoOfertaTipo | string): string {
  if (t === "portabilidade") return "🔁 Portabilidade";
  if (t === "refinanciamento") return "🔄 Refinanciamento";
  if (t === "cartao_consignado") return "💳 Cartão consignado";
  if (t === "cartao_beneficio") return "🎫 Cartão benefício";
  return "💰 Crédito novo";
}

export function BancoOfertas() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<BancoOferta | "new" | null>(null);
  const [tab, setTab] = useState<TabKey>("ativas");
  const [search, setSearch] = useState("");
  // TIPO DE PRODUTO como recorte proprio. Ate 04/09/2026 o tipo era subtitulo da
  // coluna Titulo e nao dava para recortar: comparar as taxas de portabilidade
  // exigia varrer a lista com o olho, ou buscar por texto no titulo — que so'
  // acha se quem cadastrou escreveu "portabilidade" no nome da oferta.
  const [tipo, setTipo] = useState<"" | BancoOfertaTipo>("");
  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(POR_PAGINA_PADRAO);
  const buscaDebounced = useDebouncedValue(search);
  const ofertasQ = useQuery({
    queryKey: ["banco", "ofertas", tab, tipo, buscaDebounced, pagina, porPagina],
    queryFn: () => atlas.banco.ofertas.list({
      aba: tab,
      tipo: tipo || undefined,
      q: buscaDebounced || undefined,
      limit: porPagina,
      offset: (pagina - 1) * porPagina,
    }),
    placeholderData: (anterior) => anterior,
  });
  const conveniosQ = useQuery({ queryKey: ["banco", "convenios"], queryFn: () => atlas.banco.convenios() });

  const pausar = useMutation({
    mutationFn: (id: string) => atlas.banco.ofertas.pausar(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["banco", "ofertas"] }),
  });
  const reativar = useMutation({
    mutationFn: (id: string) => atlas.banco.ofertas.reativar(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["banco", "ofertas"] }),
  });

  // A aba virou FILTRO DO SERVIDOR (`?aba=`). Antes a tela baixava todas as
  // ofertas e as separava aqui com `estaAtiva`; com o recorte no servidor isso
  // separaria apenas a pagina corrente — a aba "Encerradas" mostraria so as
  // encerradas que CALHASSEM de estar nas 25 linhas trazidas.
  //
  // A decisao "esta no ar" tambem mudou de relogio: era o `new Date()` do
  // navegador, agora e o `now()` do Postgres. E uma pergunta sobre o instante
  // presente, e o relogio do cliente nao e fonte para isso.
  const daPagina = recorteDoServidor(
    ofertasQ.data?.ofertas ?? [],
    ofertasQ.data?.total ?? 0,
    pagina,
    porPagina,
  );

  /** Trocar aba ou busca com a posicao antiga cairia num offset que o novo
   *  recorte pode nem ter. */
  const filtros = useFiltros(
    [
      { nome: "busca", ligado: search !== "", limpar: () => setSearch("") },
      // A aba tem DEFAULT NAO-VAZIO ("ativas"): `ligado` e' o desvio do default
      // e `limpar` RESTAURA, nao esvazia. Reset que abrisse as duas abas
      // mudaria o universo em vez de voltar ao normal.
      { nome: "situacao", ligado: tab !== "ativas", limpar: () => setTab("ativas") },
      // `produto` e nao `tipo`: o inventario fala o vocabulario do dominio e a
      // tela rotula "Produto"; `tipo` e' so' o nome do parametro da rota.
      { nome: "produto", ligado: tipo !== "", limpar: () => setTipo("") },
    ],
    setPagina,
  );
  const { filtrar } = filtros;

  const contagens = ofertasQ.data?.contagens ?? { ativas: 0, encerradas: 0 };
  const porTipo = ofertasQ.data?.porTipo ?? [];

  const columns: Column<BancoOferta>[] = [
    {
      key: "situacao",
      header: "Situação",
      render: (o) => {
        if (o.ativo && (!o.expiraEm || new Date(o.expiraEm) > new Date())) {
          return <Pill variant="averbado">Ativa</Pill>;
        }
        if (!o.ativo) return <Pill variant="expirado">Encerrada</Pill>;
        return <Pill variant="expirado">Expirada</Pill>;
      },
    },
    {
      key: "titulo",
      header: "Título",
      render: (o) => (
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            {o.icone ? <span style={{ fontSize: 16 }}>{o.icone}</span> : null}
            <span>{o.titulo}</span>
          </span>
          <span style={{ fontSize: 10, letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--text-dim)" }}>
            {rotuloTipo(o.tipo)}
          </span>
        </div>
      ),
    },
    { key: "taxa", header: "Taxa a.m.", align: "right", render: (o) => `${(o.taxaAm * 100).toFixed(2)}%` },
    { key: "prazo", header: "Prazo máx.", align: "right", render: (o) => `${o.parcelasMax}x` },
    { key: "valor", header: "Valor máx.", align: "right", render: (o) => fmtBRL(o.valorMax) },
    // Publico-alvo separado em colunas proprias (cliente 30/07/2026): em vez de
    // um "Público-alvo" com tudo junto, "Salário mín." e "Idade mín." cada um na
    // sua coluna. "—" (dim) quando a oferta nao filtra por aquele criterio.
    {
      key: "salarioMin",
      header: "Salário mín.",
      align: "right",
      render: (o) => o.filtro.salarioMin != null
        ? fmtReais(o.filtro.salarioMin)
        : <span style={{ color: "var(--text-dim)" }}>—</span>,
    },
    {
      key: "idadeMin",
      header: "Idade mín.",
      align: "right",
      render: (o) => o.filtro.idadeMin != null
        ? `${o.filtro.idadeMin} anos`
        : <span style={{ color: "var(--text-dim)" }}>—</span>,
    },
    { key: "criadoEm", header: "Criada em", render: (o) => new Date(o.criadoEm).toLocaleDateString("pt-BR") },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <header style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 16 }}>
        <div>
          <span style={{ fontSize: 12, letterSpacing: "0.1em", fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase" }}>
            Marketing
          </span>
          <h1 style={{ margin: "4px 0 0", fontSize: "1.8rem" }}>Ofertas</h1>
          <p style={{ color: "var(--text-muted)", margin: "6px 0 0", maxWidth: 640 }}>
            Crie ofertas de crédito filtradas por perfil. Servidores compatíveis recebem a oferta no sino do app; ao clicar, podem iniciar a proposta com você.
          </p>
        </div>
        <Button onClick={() => setEditing("new")}>+ Nova oferta</Button>
      </header>

      <FilterBar
        layout="linha"
        searchValue={search}
        onSearchChange={filtrar(setSearch)}
        searchPlaceholder="Buscar por título ou mensagem"
        {...filtros.propsFilterBar}
        actions={
          /* Cinco tipos fixos, e os chips mostram so' os que EXISTEM no recorte
             — a contagem vem da rota (`porTipo`) com semantica `exceto`, e ela
             respeita a aba: quem esta em "Encerradas" ve os tipos que ha ali.
             Oferta legada sem tipo ja chega contada como "credito_novo", entao
             nunca aparece um chip sem nome. */
          porTipo.length > 1 ? (
            <FilterChips
              label="Produto"
              value={tipo}
              onChange={(v) => filtrar(setTipo)(v as "" | BancoOfertaTipo)}
              options={[
                { value: "", label: "Todos", count: porTipo.reduce((s, t) => s + t.contagem, 0) },
                ...porTipo.map((t) => ({
                  value: t.valor,
                  label: rotuloTipo(t.valor as BancoOfertaTipo),
                  count: t.contagem,
                })),
              ]}
            />
          ) : null
        }
      />

      {/* Tabs Ativas / Encerradas — nunca ha hard-delete, so muda de aba. */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {/* As contagens vem da API: com o recorte no servidor a tela nao tem a
            outra aba em memoria. Sao do mesmo recorte de convenio/busca, sem o
            filtro da propria aba — que e o que as torna honestas. */}
        <TabBtn active={tab === "ativas"} onClick={() => filtrar(setTab)("ativas")} label={`Ativas (${contagens.ativas})`} />
        <TabBtn active={tab === "encerradas"} onClick={() => filtrar(setTab)("encerradas")} label={`Encerradas (${contagens.encerradas})`} />
      </div>

      <DataTable
        columns={columns}
        rows={daPagina.itens}
        rowKey={(o) => o.id}
        loading={ofertasQ.isLoading}
        emptyState={tab === "ativas" ? "Nenhuma oferta ativa. Crie uma no botão acima." : "Nenhuma oferta encerrada."}
        actions={(o) => (
          <>
            <IconButton title="Editar" onClick={() => setEditing(o)}>✎</IconButton>
            {tab === "ativas" ? (
              <IconButton
                title="Excluir (move pra aba Encerradas — sem apagar)"
                danger
                onClick={() => {
                  if (confirm(`Encerrar "${o.titulo}"?\n\nEla para de aparecer no sino dos servidores e vai pra aba "Encerradas". Você ainda pode reativar depois.`)) {
                    pausar.mutate(o.id);
                  }
                }}
              >
                🗑
              </IconButton>
            ) : (
              <IconButton title="Reativar (volta pra Ativas)" onClick={() => reativar.mutate(o.id)}>▶</IconButton>
            )}
          </>
        )}
      />

      <Pagination
        total={daPagina.total}
        pagina={daPagina.pagina}
        porPagina={porPagina}
        onPagina={setPagina}
        onPorPagina={setPorPagina}
        rotulo="ofertas"
      />

      {editing ? (
        <OfertaModal
          initial={editing === "new" ? null : editing}
          convenios={conveniosQ.data?.convenios ?? []}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </div>
  );
}

function OfertaModal({
  initial, convenios, onClose,
}: {
  initial: BancoOferta | null;
  // `maxParcelas` vem do SDK como `number | null`: null = convenio sem teto
  // proprio, e ai vale o teto da plataforma (120), aplicado no `?? 120` abaixo.
  // O tipo aqui era so `number` e nao acompanhou o SDK quando ele passou a
  // declarar o nulo — travava `tsc --noEmit`, e com ele o build de producao.
  convenios: { id: string; nome: string; maxParcelas?: number | null }[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  /**
   * Teto de parcelas da oferta = o do convênio MAIS RESTRITIVO entre os que
   * ela atinge. Sem filtro de convênio, a oferta vale para todos os do banco,
   * então o teto é o menor de todos. Mesma regra aplicada no backend
   * (tetoParcelasEntre, em portal-banco/limites.ts) — aqui é só para o campo
   * avisar antes de o operador salvar e levar 422.
   */
  const alvos = (initial?.filtro?.convenioIds?.length
    ? convenios.filter((cv) => initial.filtro!.convenioIds!.includes(cv.id))
    : convenios);
  const tetoParcelas = alvos.reduce((menor, cv) => Math.min(menor, cv.maxParcelas ?? 120), 120);
  const conveniosDoTeto = alvos.length === convenios.length ? "de todos os seus convênios" : "dos convênios selecionados";
  const [form, setForm] = useState<BancoOfertaInput>({
    id: initial?.id,
    titulo: initial?.titulo ?? "",
    mensagem: initial?.mensagem ?? "",
    // taxaAm e' guardado em FRACAO (0.0179 = 1.79% a.m.), mesma convencao de
    // contratos/tabelas e do que o servidor renderiza. O input abaixo edita em
    // PERCENT (mais natural), entao aqui converte fracao->percent pra exibir e o
    // submit converte percent->fracao de volta.
    taxaAm: initial ? Number((initial.taxaAm * 100).toFixed(4)) : 1.79,
    parcelasMax: initial?.parcelasMax ?? 84,
    valorMax: initial?.valorMax ?? 50000,
    ativo: initial?.ativo ?? true,
    expiraEm: initial?.expiraEm ?? "",
    filtro: initial?.filtro ?? {},
    icone: initial?.icone ?? "",
    tipo: initial?.tipo ?? "credito_novo",
  });
  const [error, setError] = useState<string | null>(null);
  const [duracaoHoras, setDuracaoHoras] = useState<string>("");
  const save = useMutation({
    mutationFn: () => {
      const horas = Number(duracaoHoras);
      const expiraCalc = duracaoHoras && Number.isFinite(horas) && horas > 0
        ? new Date(Date.now() + horas * 3600 * 1000).toISOString()
        : (form.expiraEm || undefined);
      return atlas.banco.ofertas.upsert({
        ...form,
        // Input edita em percent; persiste em fracao (0..1) — canonico do sistema.
        taxaAm: form.taxaAm / 100,
        expiraEm: expiraCalc,
        filtro: form.filtro ?? {},
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["banco", "ofertas"] });
      onClose();
    },
    onError: (err) => setError(err instanceof Error ? err.message : "Erro ao salvar"),
  });

  function toggleArrayItem(key: keyof BancoOfertaFiltro, item: string | number): void {
    setForm((f) => {
      const cur = (f.filtro?.[key] as (string | number)[] | undefined) ?? [];
      const has = cur.includes(item);
      const next = has ? cur.filter((x) => x !== item) : [...cur, item];
      return { ...f, filtro: { ...f.filtro, [key]: next.length ? next : undefined } };
    });
  }

  const filtro = form.filtro ?? {};

  return (
    <div onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }} style={modalBackdrop}>
      <div onClick={(e) => e.stopPropagation()} style={modalCard}>
        <h3 style={{ margin: 0 }}>{initial ? `Editar ${initial.titulo}` : "Nova oferta"}</h3>

        <section style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <SectionHead>Conteúdo da oferta</SectionHead>

          {/* Tipo de produto: muda o CTA no card do servidor. */}
          <div>
            <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 6 }}>
              Tipo de produto
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 8 }}>
              <TipoOptionCard
                on={form.tipo === "credito_novo" || !form.tipo}
                icone="💰"
                titulo="Crédito novo"
                descricao="Empréstimo consignado. CTA leva o servidor pro fluxo de aceite do contrato."
                onClick={() => setForm({ ...form, tipo: "credito_novo" })}
              />
              <TipoOptionCard
                on={form.tipo === "portabilidade"}
                icone="🔁"
                titulo="Portabilidade (em manutenção)"
                descricao="Temporariamente indisponível enquanto a funcionalidade passa por manutenção."
                onClick={() => setError("A portabilidade está temporariamente indisponível para manutenção.")}
              />
              <TipoOptionCard
                on={form.tipo === "refinanciamento"}
                icone="🔄"
                titulo="Refinanciamento"
                descricao="Renegociar contrato existente com esta consignatária. Só aparece pra servidores que já têm contrato ativo com você."
                onClick={() => setForm({ ...form, tipo: "refinanciamento" })}
              />
              <TipoOptionCard
                on={form.tipo === "cartao_consignado"}
                icone="💳"
                titulo="Cartão consignado"
                descricao="Cartão de crédito com fatura mínima descontada em folha. Usa a margem de cartão consignado, pela regra do órgão. Só aparece pra quem tem espaço."
                onClick={() => setForm({ ...form, tipo: "cartao_consignado" })}
              />
              <TipoOptionCard
                on={form.tipo === "cartao_beneficio"}
                icone="🎫"
                titulo="Cartão benefício"
                descricao="Cartão restrito (farmácia/mercado/saúde) descontado em folha. Usa a margem de cartão benefício, pela regra do órgão."
                onClick={() => setForm({ ...form, tipo: "cartao_beneficio" })}
              />
            </div>
          </div>

          {/* Seletor de icone (emoji) — opcional. Aparece antes do titulo no card do servidor. */}
          <div>
            <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 6 }}>
              Ícone <span style={{ color: "var(--text-dim)" }}>(opcional — aparece no card do servidor)</span>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              <IconeChip
                on={!form.icone}
                onClick={() => setForm({ ...form, icone: "" })}
                title="Sem ícone"
              >
                <span style={{ fontSize: 13, color: "var(--text-dim)" }}>—</span>
              </IconeChip>
              {ICONES.map((i) => (
                <IconeChip
                  key={i.emoji}
                  on={form.icone === i.emoji}
                  onClick={() => setForm({ ...form, icone: i.emoji })}
                  title={i.nome}
                >
                  <span style={{ fontSize: 20 }}>{i.emoji}</span>
                </IconeChip>
              ))}
            </div>
          </div>

          <TextField label="Título" value={form.titulo} onChange={(e) => setForm({ ...form, titulo: e.target.value })} placeholder="Ex.: Crédito consignado com taxa promocional" required />
          <TextField label="Mensagem (aparece no sino)" value={form.mensagem} onChange={(e) => setForm({ ...form, mensagem: e.target.value })} placeholder="Ex.: Aproveite: 1,79% a.m. em até 84x." required />
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
            <NumberField label="Taxa a.m. (%)" value={form.taxaAm} onChange={(e) => setForm({ ...form, taxaAm: Number(e.target.value) })} />
            {/* Teto da prefeitura — o campo era livre e aceitava qualquer
                numero. Um convenio de 24x podia receber oferta de 120x: o
                servidor clicava na promocao e a simulacao dele recusava o
                prazo, sem explicacao possivel. A tabela de emprestimos ja
                respeitava esse teto; a oferta tinha ficado de fora.
                Cliente 12/08/2026, item 7 da lista. */}
            <NumberField
              label="Prazo máx. (x)"
              value={form.parcelasMax}
              min={1}
              max={tetoParcelas}
              onChange={(e) => setForm({ ...form, parcelasMax: Number(e.target.value) })}
              hint={`Teto ${conveniosDoTeto} : ${tetoParcelas} parcelas.`}
              error={form.parcelasMax > tetoParcelas ? `Acima do teto de ${tetoParcelas} parcelas.` : undefined}
            />
            <NumberField label="Valor máx. (R$)" value={form.valorMax} onChange={(e) => setForm({ ...form, valorMax: Number(e.target.value) })} />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <TextField
              label="Expira em (opcional)"
              type="date"
              value={form.expiraEm ?? ""}
              onChange={(e) => setForm({ ...form, expiraEm: e.target.value })}
              hint="Ideal para ofertas mensais/sazonais."
              disabled={!!duracaoHoras && Number(duracaoHoras) > 0}
            />
            <TextField
              label="Ou duração em horas (promoção relâmpago)"
              type="number"
              min="0"
              step="0.5"
              value={duracaoHoras}
              onChange={(e) => setDuracaoHoras(e.target.value)}
              placeholder="Ex.: 24"
              hint="Se preenchido, começa a contar a partir de agora."
            />
          </div>
          {duracaoHoras && Number(duracaoHoras) > 0 ? (
            <div style={{ padding: "8px 12px", borderRadius: 8, border: "1px dashed var(--gold-500)", background: "color-mix(in srgb, var(--gold-500) 10%, transparent)", fontSize: 12 }}>
              ⏱ Promoção relâmpago: expira em <b>{new Date(Date.now() + Number(duracaoHoras) * 3600 * 1000).toLocaleString("pt-BR")}</b>. O servidor vê um contador regressivo.
            </div>
          ) : null}
        </section>

        <section style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <SectionHead>Público-alvo (deixe vazio para atingir todos os elegíveis)</SectionHead>

          <FieldGroup label="Convênios">
            {convenios.length === 0 ? <span style={{ color: "var(--text-dim)", fontSize: 13 }}>Nenhum convênio ativo.</span> : convenios.map((cv) => {
              const on = filtro.convenioIds?.includes(cv.id) ?? false;
              return <Chip key={cv.id} on={on} onClick={() => toggleArrayItem("convenioIds", cv.id)}>{cv.nome}</Chip>;
            })}
          </FieldGroup>

          <FieldGroup label="Vínculos">
            {VINCULOS.map((v) => {
              const on = filtro.vinculos?.includes(v) ?? false;
              return <Chip key={v} on={on} onClick={() => toggleArrayItem("vinculos", v)}>{v}</Chip>;
            })}
          </FieldGroup>

          <FieldGroup label="Situação funcional">
            {SITUACOES.map((s) => {
              const on = filtro.situacaoFuncional?.includes(s) ?? false;
              return <Chip key={s} on={on} onClick={() => toggleArrayItem("situacaoFuncional", s)}>{s}</Chip>;
            })}
          </FieldGroup>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <TextField
              label="Salário mín. (R$)"
              type="number"
              value={filtro.salarioMin != null ? String(filtro.salarioMin) : ""}
              onChange={(e) => setForm({ ...form, filtro: { ...filtro, salarioMin: e.target.value ? Number(e.target.value) : undefined } })}
            />
            <TextField
              label="Salário máx. (R$)"
              type="number"
              value={filtro.salarioMax != null ? String(filtro.salarioMax) : ""}
              onChange={(e) => setForm({ ...form, filtro: { ...filtro, salarioMax: e.target.value ? Number(e.target.value) : undefined } })}
            />
            <TextField
              label="Idade mín."
              type="number"
              value={filtro.idadeMin != null ? String(filtro.idadeMin) : ""}
              onChange={(e) => setForm({ ...form, filtro: { ...filtro, idadeMin: e.target.value ? Number(e.target.value) : undefined } })}
            />
            <TextField
              label="Idade máx."
              type="number"
              value={filtro.idadeMax != null ? String(filtro.idadeMax) : ""}
              onChange={(e) => setForm({ ...form, filtro: { ...filtro, idadeMax: e.target.value ? Number(e.target.value) : undefined } })}
            />
          </div>
        </section>

        <label style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13, color: "var(--text-muted)" }}>
          <input type="checkbox" checked={form.ativo ?? true} onChange={(e) => setForm({ ...form, ativo: e.target.checked })} />
          Publicar imediatamente (aparece no sino dos servidores compatíveis)
        </label>

        {error ? <div style={{ color: "var(--danger-500)", fontSize: 13 }}>{error}</div> : null}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button disabled={save.isPending || !form.titulo || !form.mensagem} onClick={() => save.mutate()}>{save.isPending ? "Salvando..." : "Salvar oferta"}</Button>
        </div>
      </div>
    </div>
  );
}

function SectionHead({ children }: { children: React.ReactNode }) {
  return <div style={{ fontSize: 11, letterSpacing: ".08em", textTransform: "uppercase", fontWeight: 700, color: "var(--text-dim)" }}>{children}</div>;
}
function FieldGroup({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 6 }}>{label}</div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>{children}</div>
    </div>
  );
}
function Chip({ on, onClick, children }: { on: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: "5px 10px",
        borderRadius: 999,
        border: "1px solid",
        borderColor: on ? "var(--emerald-500)" : "var(--border-strong)",
        background: on ? "var(--emerald-500)" : "transparent",
        color: on ? "var(--navy-900)" : "var(--text)",
        cursor: "pointer",
        fontSize: 12,
        fontWeight: 600,
      }}
    >
      {children}
    </button>
  );
}

function TipoOptionCard({
  on, icone, titulo, descricao, onClick,
}: {
  on: boolean; icone: string; titulo: string; descricao: string; onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        textAlign: "left",
        padding: 12,
        borderRadius: 10,
        border: "1px solid",
        borderColor: on ? "var(--emerald-500)" : "var(--border-strong)",
        background: on ? "color-mix(in srgb, var(--emerald-500) 10%, transparent)" : "transparent",
        cursor: "pointer",
        display: "flex",
        gap: 10,
        alignItems: "flex-start",
      }}
    >
      <span style={{ fontSize: 20 }}>{icone}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text)" }}>{titulo}</div>
        <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2, lineHeight: 1.4 }}>{descricao}</div>
      </div>
    </button>
  );
}

function IconeChip({ on, onClick, title, children }: { on: boolean; onClick: () => void; title: string; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      style={{
        width: 40, height: 40,
        display: "flex", alignItems: "center", justifyContent: "center",
        borderRadius: 8,
        border: "1px solid",
        borderColor: on ? "var(--emerald-500)" : "var(--border-strong)",
        background: on ? "color-mix(in srgb, var(--emerald-500) 15%, transparent)" : "transparent",
        cursor: "pointer",
      }}
    >
      {children}
    </button>
  );
}

function TabBtn({ active, onClick, label }: { active: boolean; onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: "8px 16px",
        borderRadius: 10,
        border: `1px solid ${active ? "var(--emerald-500)" : "var(--border)"}`,
        background: active ? "color-mix(in srgb, var(--emerald-500) 10%, transparent)" : "transparent",
        color: active ? "var(--emerald-500)" : "var(--text-muted)",
        fontSize: 13,
        fontWeight: 700,
        cursor: "pointer",
      }}
    >
      {label}
    </button>
  );
}

const modalBackdrop: React.CSSProperties = {
  position: "fixed", inset: 0, background: "rgba(10,22,40,.6)",
  display: "grid", placeItems: "center", zIndex: 100, backdropFilter: "blur(6px)", padding: 16,
};
const modalCard: React.CSSProperties = {
  background: "var(--bg-elev)", border: "1px solid var(--border-strong)",
  borderRadius: 14, padding: 24, maxWidth: 640, width: "calc(100% - 32px)",
  display: "flex", flexDirection: "column", gap: 16, boxShadow: "var(--shadow-lg)",
  maxHeight: "calc(100vh - 32px)", overflowY: "auto",
};
